import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import clsx from 'clsx';
import { Suspense, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import ChevronDown from '@/assets/icons/chevron-down.svg';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { queryKeys } from '@/query-keys';
import { Skeleton } from '@/components/ui/skeleton';
import { useAddContractContext } from '@/contexts/add-contract-context';
import { useIndeedPanelParams } from '@/hooks/use-indeed-panel-params';
import { useSourceFreemium } from '@/hooks/use-source-freemium';
import { useSourceIndeedContract } from '@/hooks/use-source-indeed-contract';
import { reportBoundaryError } from '@/utils/sentry';

import { usePopularContracts } from '../popular-contracts';
import {
  ChannelSearchDropdown,
  type ChannelOption,
} from './channel-search-dropdown';

const CHANNEL_SEARCH_DEBOUNCE_MS = 300;

interface ChannelSearchSelectProps {
  loading?: boolean;
  selectedChannelId?: number;
  selectedChannelName?: string;
  onChannelChange: (channelId: number | undefined, name?: string) => void;
}

export function ChannelSearchSelect(props: ChannelSearchSelectProps) {
  const enabled = useSourceIndeedContract();
  const isFreemium = useSourceFreemium();
  if (enabled && !isFreemium && props.loading) {
    return <Skeleton className="h-10 w-full" />;
  }
  return enabled && !isFreemium ? (
    <ErrorBoundary
      onError={reportBoundaryError('settings.contract-channel-search')}
      fallback={<ChannelSearchInput {...props} />}
    >
      <Suspense fallback={<Skeleton className="h-10 w-full" />}>
        <PopularChannelSearchSelect {...props} />
      </Suspense>
    </ErrorBoundary>
  ) : (
    <ChannelSearchInput {...props} />
  );
}

function PopularChannelSearchSelect(props: ChannelSearchSelectProps) {
  const popularContracts = usePopularContracts();
  const { setSheetOpen } = useAddContractContext();
  const { open } = useIndeedPanelParams();
  return (
    <ChannelSearchInput
      {...props}
      popularOptions={popularContracts.map(board => ({
        ...board,
        logoSquareUrl: null,
      }))}
      onIndeedSelect={() => {
        setSheetOpen(false);
        open();
      }}
    />
  );
}

function ChannelSearchInput({
  selectedChannelId,
  selectedChannelName,
  onChannelChange,
  popularOptions,
  onIndeedSelect,
}: ChannelSearchSelectProps & {
  popularOptions?: ChannelOption[];
  onIndeedSelect?: () => void;
}) {
  const intl = useIntl();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number>();
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const enhanced = popularOptions !== undefined;
  const searchLabel = enhanced
    ? intl.formatMessage({
        defaultMessage: 'Select board',
        description: 'Add contract sheet: label for channel search field',
      })
    : undefined;

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(inputValue),
      CHANNEL_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (selectedChannelName) {
      setInputValue(selectedChannelName);
    }
  }, [selectedChannelId, selectedChannelName]);

  const trimmedSearch = debouncedSearchQuery.trim();
  const shouldFetch = open && trimmedSearch.length > 0;
  const { data, isFetching } = useQuery({
    queryKey: [...queryKeys.settings.contracts.vonqChannels, trimmedSearch],
    queryFn: () =>
      SourcingService.getInternalApiSourcingVonqContractsChannels(
        trimmedSearch,
      ),
    enabled: shouldFetch,
  });

  const channelOptions = useMemo(() => {
    if (!shouldFetch) return [];

    return (data?.channels ?? []).map(channel => ({
      id: channel.id,
      name: channel.name,
      logoSquareUrl: channel.logo_square_url,
    }));
  }, [data?.channels, shouldFetch]);

  const hasSearchQuery = inputValue.trim().length > 0;
  const isDebouncingSearch =
    hasSearchQuery && inputValue !== debouncedSearchQuery;
  const isFetchingFromApi = shouldFetch && isFetching;
  const search = inputValue.trim().toLowerCase();
  const indeedOption = popularOptions?.find(board => board.name === 'Indeed');
  const suggestIndeed =
    indeedOption &&
    search.length >= 2 &&
    ('indeed'.startsWith(search) || search.includes('indeed'));
  const searchOptions = suggestIndeed
    ? [indeedOption, ...channelOptions]
    : channelOptions;
  const isLoading = hasSearchQuery && (isDebouncingSearch || isFetchingFromApi);
  const options = enhanced && !hasSearchQuery ? popularOptions : searchOptions;
  const activeOption = !isLoading && open ? options[activeIndex] : undefined;
  const activeOptionId = activeOption
    ? `${listboxId}-${activeIndex}`
    : undefined;

  const handleOpenDropdown = () => {
    setActiveIndex(-1);
    setOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOpen(true);
    setInputValue(e.target.value);
    setActiveIndex(-1);
    onChannelChange(undefined, undefined);
  };

  const handleSelectChannel = (channelId: number | undefined, name: string) => {
    if (enhanced && name === 'Indeed') {
      setOpen(false);
      onIndeedSelect?.();
      return;
    }
    setInputValue(name);
    onChannelChange(channelId, name);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (isLoading || options.length === 0) return;
      const direction = e.key === 'ArrowDown' ? 1 : -1;
      const nextIndex =
        !open || activeIndex < 0
          ? direction === 1
            ? 0
            : options.length - 1
          : (activeIndex + direction + options.length) % options.length;
      setActiveIndex(nextIndex);
      document
        .getElementById(`${listboxId}-${nextIndex}`)
        ?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (activeOption) handleSelectChannel(activeOption.id, activeOption.name);
    }
  };

  useEffect(() => {
    if (open && anchorRef.current) {
      setPopoverWidth(anchorRef.current.offsetWidth);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          className="relative w-full"
          data-contract-search-open={(enhanced && open) || undefined}
        >
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleOpenDropdown}
            onFocus={enhanced ? handleOpenDropdown : undefined}
            onKeyDown={enhanced ? handleKeyDown : undefined}
            role={enhanced ? 'combobox' : undefined}
            aria-label={searchLabel}
            aria-expanded={enhanced ? open : undefined}
            aria-activedescendant={enhanced ? activeOptionId : undefined}
            placeholder={intl.formatMessage({
              defaultMessage: 'Type a contract name',
              description:
                'Placeholder for the job channel search input in add contract sheet',
            })}
            className={clsx(
              'placeholder:text-fx-neutral-600 text-fx-neutral-900 h-10 w-full rounded-sm border bg-white py-2 pr-8 pl-2 text-sm transition-[color,box-shadow] outline-none',
              'border-fx-neutral-300',
              'focus:border-fx-brand-600 focus:shadow-selected',
              open && 'border-fx-brand-600 shadow-selected',
            )}
            aria-autocomplete="list"
            aria-controls={open ? listboxId : undefined}
          />
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="text-fx-neutral-400 hover:text-fx-neutral-600 pointer-events-auto absolute inset-y-0 right-0 flex w-8 items-center justify-center"
            onClick={e => {
              e.preventDefault();
              handleOpenDropdown();
              inputRef.current?.focus();
            }}
          >
            <img src={ChevronDown} alt="" className="h-4 w-4" />
          </button>
        </div>
      </PopoverAnchor>
      {open && (
        <PopoverContent
          className="border-fx-neutral-200 pointer-events-auto z-50 overflow-hidden rounded-sm border bg-white !p-1 shadow-lg outline-none"
          align="start"
          sideOffset={2}
          style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
          onOpenAutoFocus={e => e.preventDefault()}
          data-contract-search-open={enhanced || undefined}
          onEscapeKeyDown={
            enhanced
              ? event => {
                  event.preventDefault();
                  inputRef.current?.focus();
                  setOpen(false);
                }
              : undefined
          }
          onInteractOutside={e => {
            if (inputRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <div className="max-h-[280px] overflow-y-auto leading-none">
            <ChannelSearchDropdown
              isDebouncing={hasSearchQuery && isDebouncingSearch}
              isFetching={hasSearchQuery && isFetchingFromApi}
              hasSearchQuery={hasSearchQuery}
              options={options}
              showPopular={enhanced && !hasSearchQuery}
              keyboardEnabled={enhanced}
              activeIndex={activeOption ? activeIndex : -1}
              selectedChannelId={selectedChannelId}
              onSelect={handleSelectChannel}
              listboxId={listboxId}
              listboxLabel={searchLabel}
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
